/**
 * CatalogueAuditPage — Iter259
 * ────────────────────────────
 * Dedicated workspace for filling in the structured fields on rate
 * catalogue rows. Lives at /catalogue-audit/<componentType>. Reads
 * /api/catalogue-audit/<componentType> for the full row list +
 * completeness stats, patches one row at a time on field-blur.
 *
 * Schema source-of-truth: /app/memory/CATALOGUE_SCHEMA.md.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Per-component schema: backend whitelists the field keys; the
// OPTIONS for pill/dropdown fields are fetched from the API so the
// operator can add/rename/delete them on the fly (Iter259c).
const SCHEMAS = {
  aquaseal: {
    componentType: 'Aquaseal',
    displayLabel: 'Dektite',
    fields: [
      { key: 'dektite_class', label: 'Class',    kind: 'pill',     required: true },
      { key: 'pipe_kind',     label: 'Kind',     kind: 'pill',     required: true },
      { key: 'material',      label: 'Material', kind: 'dropdown', required: true },
      { key: 'size_min_mm', label: 'Ø min (mm)', kind: 'number' },
      { key: 'size_max_mm', label: 'Ø max (mm)', kind: 'number' },
      { key: 'has_clips',   label: 'With clips', kind: 'bool' },
      { key: 'usage_note',  label: 'Usage note', kind: 'text', placeholder: 'e.g. Toilet Breather' },
      { key: 'is_accessory', label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  // Iter260d — Sheet / Barge / Flashing audit pages. Schemas mirror
  // the structured fields populated by the backend's bulk auto-fill
  // (parse_sheet_name / parse_barge_name / parse_flashing_name).
  sheet: {
    componentType: 'Sheet',
    displayLabel: 'Sheet',
    fields: [
      { key: 'sheet_profiles', label: 'Profile',     kind: 'multipill', required: true },
      { key: 'material',       label: 'Material',    kind: 'dropdown', required: true },
      { key: 'thickness_mm',   label: 'Gauge (mm)',  kind: 'number',   required: true, placeholder: 'e.g. 0.40' },
      { key: 'cover_width_mm', label: 'Cover (mm)',  kind: 'number',   required: true, placeholder: 'e.g. 762' },
      { key: 'unit',           label: 'Unit',        kind: 'pill',     required: true },
      { key: 'usage_note',     label: 'Usage note',  kind: 'text',     placeholder: 'e.g. Specified by manufacturing weight, not gauge' },
      { key: 'is_accessory',   label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  barge: {
    componentType: 'Barge',
    displayLabel: 'Barge',
    fields: [
      { key: 'barge_profiles', label: 'Barge profile', kind: 'multipill', required: true },
      { key: 'tile_profile',  label: 'Tile profile (fits)', kind: 'multipill' },
      { key: 'work_types',    label: 'Work types', kind: 'multipill',   required: true },
      { key: 'material',      label: 'Material',  kind: 'dropdown',     required: true },
      { key: 'family',        label: 'Family',    kind: 'pill',         required: true },
      { key: 'girth_mm',      label: 'Girth (mm)', kind: 'number', placeholder: 'e.g. 300' },
      { key: 'folds_count',   label: 'Folds',    kind: 'number',   placeholder: '0–6' },
      { key: 'is_accessory',  label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  flashing: {
    componentType: 'Flashing',
    displayLabel: 'Flashing',
    fields: [
      { key: 'material',          label: 'Material',  kind: 'dropdown', required: true },
      { key: 'girth_mm',          label: 'Girth (mm)', kind: 'number',  required: true, placeholder: 'e.g. 235' },
      { key: 'folds_count',       label: 'Folds',     kind: 'number',   required: true, placeholder: '0–6' },
      { key: 'flashing_category', label: 'Category',  kind: 'pill' },
      { key: 'is_accessory',      label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  // Iter260i — 5 additional audit pages.
  ridge: {
    componentType: 'Ridge',
    displayLabel: 'Ridge',
    fields: [
      { key: 'ridge_profiles', label: 'Ridge profile', kind: 'multipill', required: true },
      { key: 'tile_profile',  label: 'Tile profile (fits)', kind: 'multipill' },
      { key: 'work_types',    label: 'Work types', kind: 'multipill',   required: true },
      { key: 'material',      label: 'Material',  kind: 'dropdown',     required: true },
      { key: 'family',        label: 'Family',    kind: 'pill',         required: true },
      { key: 'girth_mm',      label: 'Girth (mm)', kind: 'number' },
      { key: 'folds_count',   label: 'Folds',    kind: 'number' },
      { key: 'is_accessory',  label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  tile: {
    componentType: 'Tile',
    displayLabel: 'Tile',
    fields: [
      { key: 'tile_profiles', label: 'Tile profile', kind: 'multipill', required: true },
      { key: 'material',     label: 'Material',     kind: 'dropdown', required: true },
      { key: 'family',       label: 'Family',       kind: 'pill',     required: true },
      { key: 'work_types',   label: 'Work types',   kind: 'multipill' },
      { key: 'is_accessory', label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  valley: {
    componentType: 'Valley',
    displayLabel: 'Valley',
    fields: [
      { key: 'material',         label: 'Material',  kind: 'dropdown', required: true },
      { key: 'girth_mm',         label: 'Girth (mm)', kind: 'number',  required: true },
      { key: 'folds_count',      label: 'Folds',     kind: 'number',   required: true },
      { key: 'valley_category',  label: 'Category',  kind: 'pill' },
      { key: 'is_accessory',     label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  spouting: {
    componentType: 'Spouting',
    displayLabel: 'Spouting',
    fields: [
      { key: 'spouting_profiles', label: 'Profile',   kind: 'multipill', required: true },
      { key: 'material',         label: 'Material',  kind: 'dropdown', required: true },
      { key: 'unit',             label: 'Unit',      kind: 'pill',     required: true },
      { key: 'girth_mm',         label: 'Girth (mm)', kind: 'number' },
      { key: 'is_accessory',     label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  underlay: {
    componentType: 'Underlay',
    displayLabel: 'Underlay',
    fields: [
      { key: 'material',      label: 'Material',     kind: 'dropdown', required: true },
      { key: 'coverage_m2',   label: 'Coverage (m²)', kind: 'number',  required: true, placeholder: 'e.g. 50' },
      { key: 'weight_class',  label: 'Weight class', kind: 'pill' },
      { key: 'roll_width_mm', label: 'Roll width (mm)', kind: 'number', placeholder: 'e.g. 1350' },
      { key: 'is_accessory',  label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
  // Iter260p — ReCoat: surface repaint rates. Operator tags each row
  // with the surface(s) it applies to + material + grade so the
  // downstream cascade can pick the right rate per appraisal context.
  //
  // Iter276c — `recoat_type` (Roof / Misc) and `sub_reference`
  // (Gutters / Fascia / Garage Door / Fences) buckets external paint
  // jobs away from the main roof recoat list.
  recoat: {
    componentType: 'ReCoat',
    displayLabel: 'ReCoat',
    fields: [
      { key: 'recoat_type',    label: 'Recoat type',     kind: 'pill' },
      { key: 'sub_reference',  label: 'Sub-reference',   kind: 'pill' },
      { key: 'surface_types',  label: 'Surface(s)',     kind: 'multipill', required: true },
      { key: 'material',       label: 'Material',       kind: 'dropdown',  required: true },
      { key: 'coating_grade',  label: 'Coating grade',  kind: 'pill',      required: true },
      { key: 'unit',           label: 'Unit',           kind: 'pill',      required: true },
      { key: 'stages',         label: 'Stages',         kind: 'pill' },
      { key: 'steep',          label: 'Steep premium',  kind: 'bool' },
      { key: 'commercial',     label: 'Commercial tier', kind: 'bool' },
      { key: 'disc',           label: 'Discount tier',  kind: 'bool' },
      { key: 'is_accessory',   label: 'Accessory (hide from snap)', kind: 'bool' },
    ],
  },
};

const DotIndicator = ({ pct }) => {
  // Three-dot completeness pip (●●●). Maps pct to dot count, colour-coded.
  const filled = pct >= 100 ? 3 : pct >= 66 ? 2 : pct >= 33 ? 1 : 0;
  const colour = pct >= 100 ? 'text-emerald-600' : pct >= 66 ? 'text-amber-600' : 'text-rose-500';
  return (
    <span className={`font-mono text-sm ${colour}`} title={`${pct}% complete`}>
      {'●'.repeat(filled)}{'○'.repeat(3 - filled)} <span className="text-xs">{pct}%</span>
    </span>
  );
};

// Iter259d — Cascade rules per component_type. When the "trigger"
// field commits to a "value", the rules auto-extend the patch with
// the listed derived fields. `hard: true` overwrites unconditionally;
// `hard: false` only writes when the target field is currently empty.
//
// Aquaseal rules (per A5051 walkthrough):
//   • Std       → has_clips=false (hard); material=EPDM (soft)
//   • Retrofit  → has_clips=true  (hard); material=EPDM (soft)
//   • Lead      → no has_clips cascade; material=Lead (soft)
//   • Alu       → has_clips=false (hard); material=Aluminium (soft)
const CASCADE_RULES = {
  Aquaseal: {
    dektite_class: {
      std:      [{ field: 'has_clips', value: false, hard: true },
                 { field: 'material',  value: 'EPDM',    hard: false }],
      retrofit: [{ field: 'has_clips', value: true,  hard: true },
                 { field: 'material',  value: 'EPDM',    hard: false }],
      lead:     [{ field: 'material',  value: 'Lead',    hard: false }],
      alu:      [{ field: 'has_clips', value: false, hard: true },
                 { field: 'material',  value: 'Aluminium', hard: false }],
    },
  },
};

export default function CatalogueAuditPage() {
  const { component } = useParams();
  const { api } = useAuth();
  const schema = SCHEMAS[(component || '').toLowerCase()];
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, complete: 0, pct: 0 });
  const [filter, setFilter] = useState('all'); // 'all' | 'incomplete'
  // Iter260m — operator-toggleable grouping. When enabled, rows are
  // bucketed into collapsible material groups so long lists (Flashings
  // especially) can be navigated by material rather than scrolled
  // alphabetically. Open/closed state is tracked per material.
  const [groupByMaterial, setGroupByMaterial] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  // Iter259c — operator-managed option lists per pill/dropdown field.
  // Map: { dektite_class: ['std','retrofit','lead'], … }
  const [optionsByField, setOptionsByField] = useState({});
  // Iter276e — Recipe cost summary keyed by catalogue_row_id so each
  // row card can render a "🧪 $X.XX /m²" badge alongside the Recipe
  // link. Bulk-fetched once on page load.
  const [recipeCostByRowId, setRecipeCostByRowId] = useState({});

  const load = useCallback(async () => {
    if (!schema) return;
    setLoading(true);
    try {
      const [rowsRes, optsRes, costRes] = await Promise.all([
        api.get(`/catalogue-audit/${schema.componentType}`),
        api.get(`/catalogue-audit/${schema.componentType}/options`),
        api.get('/material-recipes-cost-summary').catch(() => ({ data: { summaries: [] } })),
      ]);
      setRows(rowsRes.data.rows || []);
      setStats(rowsRes.data.stats || { total: 0, complete: 0, pct: 0 });
      setOptionsByField(optsRes.data.fields || {});
      const costMap = {};
      (costRes?.data?.summaries || []).forEach((s) => {
        if (s.catalogue_row_id) costMap[s.catalogue_row_id] = s;
      });
      setRecipeCostByRowId(costMap);
    } catch (e) {
      toast.error(`Failed to load: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [schema, api]);

  useEffect(() => { load(); }, [load]);

  const visibleRows = useMemo(() => {
    if (filter === 'incomplete') return rows.filter(r => !r._completeness?.complete);
    return rows;
  }, [rows, filter]);

  const patchRow = async (rateId, key, value) => {
    try {
      // Iter260 — apply cascade rules before sending the patch so the
      // round-trip is single-shot. `hard: true` overwrites, `hard: false`
      // only fills empty fields. Cascade trigger compares lower-cased
      // string values so "Std" / "std" both match.
      const payload = { [key]: value };
      const rules = CASCADE_RULES[schema.componentType]?.[key];
      if (rules) {
        const triggered = rules[String(value || '').toLowerCase()];
        if (triggered) {
          const currentRow = rows.find(r => r._id === rateId) || {};
          triggered.forEach(rule => {
            const existing = currentRow[rule.field];
            const isEmpty = existing === undefined || existing === null || existing === '';
            if (rule.hard || isEmpty) payload[rule.field] = rule.value;
          });
        }
      }
      const { data } = await api.patch(
        `/catalogue-audit/${schema.componentType}/${rateId}`,
        payload
      );
      setRows(prev => prev.map(r => r._id === rateId ? data : r));
      // Recompute stats locally so the progress bar reacts immediately.
      setStats(prev => {
        const updated = rows.map(r => r._id === rateId ? data : r);
        const done = updated.filter(r => r._completeness?.complete).length;
        return {
          ...prev,
          complete: done,
          pct: prev.total ? Math.round(100 * done / prev.total) : 100,
        };
      });
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Iter259c — Option CRUD. Each handler updates state optimistically
  // on success so the affected pills repaint instantly.
  const addOption = async (fieldKey, value) => {
    const v = String(value || '').trim();
    if (!v) return;
    try {
      const { data } = await api.post(
        `/catalogue-audit/${schema.componentType}/options/${fieldKey}`,
        { value: v }
      );
      setOptionsByField(prev => ({ ...prev, [fieldKey]: data.options || [] }));
      toast.success(`Added "${v}"`);
    } catch (e) {
      toast.error(`Add failed: ${e?.response?.data?.detail || e.message}`);
    }
  };
  const renameOption = async (fieldKey, oldValue, newValue) => {
    const v = String(newValue || '').trim();
    if (!v || v === oldValue) return;
    try {
      const { data } = await api.patch(
        `/catalogue-audit/${schema.componentType}/options/${fieldKey}/${encodeURIComponent(oldValue)}`,
        { value: v }
      );
      setOptionsByField(prev => ({ ...prev, [fieldKey]: data.options || [] }));
      toast.success(`Renamed "${oldValue}" → "${v}" (${data.rows_updated} row${data.rows_updated === 1 ? '' : 's'} migrated)`);
      load(); // reload rows since values were cascaded
    } catch (e) {
      toast.error(`Rename failed: ${e?.response?.data?.detail || e.message}`);
    }
  };
  const deleteOption = async (fieldKey, value) => {
    if (!window.confirm(`Delete option "${value}"?`)) return;
    try {
      const { data } = await api.delete(
        `/catalogue-audit/${schema.componentType}/options/${fieldKey}/${encodeURIComponent(value)}`
      );
      setOptionsByField(prev => ({ ...prev, [fieldKey]: data.options || [] }));
      toast.success(`Deleted "${value}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  // Iter260d — Bulk "Auto-fill from type_name". Backend parses every
  // row's name with the canonical per-component parser and fills any
  // empty structured fields. Operator-set values are NEVER overwritten.
  const autoFill = async () => {
    if (!window.confirm(`Parse every ${schema.displayLabel} row's name and pre-fill empty structured fields?\n\nOperator-set values stay untouched. You can then review row-by-row.`)) return;
    try {
      const { data } = await api.post(`/catalogue-audit/${schema.componentType}/auto-fill`);
      toast.success(`Auto-filled ${data.fields_filled} field${data.fields_filled === 1 ? '' : 's'} across ${data.rows_touched} of ${data.total_rows} rows.`);
      await load();
    } catch (e) {
      toast.error(`Auto-fill failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (!schema) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-800">Catalogue Audit</h1>
        <p className="mt-4 text-slate-600">No audit schema for <code>{component}</code> yet.</p>
        <p className="mt-3 text-sm text-slate-600">Currently supported:</p>
        <ul className="mt-2 space-y-1 text-sm">
          {Object.entries(SCHEMAS).map(([key, s]) => (
            <li key={key}>
              <Link to={`/catalogue-audit/${key}`} className="text-orange-700 hover:underline">→ {s.displayLabel}</Link>
            </li>
          ))}
        </ul>
        <Link to="/component-rates" className="mt-4 inline-block text-sm text-slate-500 hover:underline" data-testid="catalogue-audit-back-link">← Component Rates Tables</Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid={`catalogue-audit-page-${schema.componentType}`}>
      {/* Header + back link */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]">{schema.displayLabel} Catalogue Audit</h1>
          <p className="text-sm text-slate-500 mt-1">Fill in the structured fields so the downstream cascades read clean data.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Iter260d — Quick-switch picker. Operator can jump between
              audit pages without leaving the workspace. */}
          <select
            value={(component || '').toLowerCase()}
            onChange={(e) => { window.location.href = `/catalogue-audit/${e.target.value}`; }}
            data-testid="catalogue-audit-switch"
            className="text-xs px-2 py-1.5 border border-slate-300 rounded font-semibold bg-white"
            title="Jump to a different catalogue audit"
          >
            {Object.entries(SCHEMAS).map(([key, s]) => (
              <option key={key} value={key}>{s.displayLabel}</option>
            ))}
          </select>
          <Link to={`/component-rates?component=${schema.componentType}`} className="text-sm text-orange-700 hover:underline" data-testid="catalogue-audit-rates-link">
            ← Component Rates Tables
          </Link>
        </div>
      </div>

      {/* Progress + filters */}
      <div className="mb-5 p-4 bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="catalogue-audit-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-slate-700">Progress</span>
          <span className="text-sm font-mono tabular-nums text-slate-600" data-testid="catalogue-audit-progress-pct">
            {stats.complete} of {stats.total} complete · {stats.pct}%
          </span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all" style={{ width: `${stats.pct}%` }} />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button
            type="button"
            onClick={() => setFilter('all')}
            data-testid="filter-all"
            className={`text-xs px-3 py-1 rounded-full font-semibold ring-1 ${filter === 'all' ? 'bg-slate-700 text-white ring-slate-700' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`}
          >All ({stats.total})</button>
          <button
            type="button"
            onClick={() => setFilter('incomplete')}
            data-testid="filter-incomplete"
            className={`text-xs px-3 py-1 rounded-full font-semibold ring-1 ${filter === 'incomplete' ? 'bg-amber-600 text-white ring-amber-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`}
          >Incomplete only ({stats.total - stats.complete})</button>
          {/* Iter260d — Bulk auto-fill button. Available on every audit page;
              parses each row's `type_name` with the canonical parser and
              fills empty structured fields. Operator-set values are never
              overwritten — safe to run repeatedly. */}
          <button
            type="button"
            onClick={autoFill}
            data-testid="catalogue-audit-auto-fill"
            className="ml-auto text-xs px-3 py-1 rounded-full font-semibold ring-1 bg-sky-600 text-white ring-sky-600 hover:bg-sky-700"
            title="Parse every row name and pre-fill empty structured fields. Doesn't overwrite anything you've already set."
          >🪄 Auto-fill from name</button>
          {/* Iter260m — Group-by-Material toggle. Long lists like
              Flashings get unwieldy alphabetically; bucketing by
              material lets the operator scan one material at a time. */}
          <button
            type="button"
            onClick={() => setGroupByMaterial(g => !g)}
            data-testid="catalogue-audit-group-by-material"
            className={`text-xs px-3 py-1 rounded-full font-semibold ring-1 ${groupByMaterial ? 'bg-violet-600 text-white ring-violet-600 hover:bg-violet-700' : 'bg-white text-violet-700 ring-violet-300 hover:bg-violet-50'}`}
            title={schema.componentType === 'ReCoat'
              ? 'Toggle grouping by Recoat type — Roof vs Misc buckets (Fascia / Gutters / Garage Door / Fences).'
              : 'Toggle grouping by material — collapsible buckets for faster audit on long lists.'}
          >📚 {(() => {
            const isRecoat = schema.componentType === 'ReCoat';
            const labelOn  = isRecoat ? 'Grouped by Recoat type' : 'Grouped by material';
            const labelOff = isRecoat ? 'Group by Recoat type'   : 'Group by material';
            return groupByMaterial ? labelOn : labelOff;
          })()}</button>
        </div>
      </div>

      {loading && <div className="text-center py-10 text-slate-500">Loading…</div>}
      {!loading && visibleRows.length === 0 && (
        <div className="text-center py-10 text-slate-500 bg-white border border-slate-200 rounded-lg">
          {filter === 'incomplete' ? '🎉 All rows complete!' : 'No rows in catalogue.'}
        </div>
      )}

      <div className="space-y-3">
        {groupByMaterial ? (
          // Iter260m — Bucket visibleRows by `material`. Empty / unset
          // values land under "— Unassigned —". Sort by material name
          // for stable ordering. Each group has a sticky header with
          // count + collapse toggle.
          //
          // Iter276c — For ReCoat specifically, bucket by
          // `recoat_type / sub_reference` instead of `material`, so
          // the Misc external-paint rows (Fascia / Garage Door /
          // Gutters / Fences) sit clearly grouped beneath the main
          // Roof bucket.
          (() => {
            const isRecoat = schema.componentType === 'ReCoat';
            const buckets = {};
            visibleRows.forEach(r => {
              let key;
              if (isRecoat) {
                const rt = (r.recoat_type || '').trim() || 'Roof';
                const sub = (r.sub_reference || '').trim();
                key = sub ? `${rt} / ${sub}` : rt;
              } else {
                key = (r.material || '— Unassigned —').trim() || '— Unassigned —';
              }
              (buckets[key] = buckets[key] || []).push(r);
            });
            const sortedKeys = Object.keys(buckets).sort((a, b) => {
              if (a === '— Unassigned —') return 1;
              if (b === '— Unassigned —') return -1;
              // For ReCoat: Roof first, then Misc buckets alphabetically.
              if (isRecoat) {
                if (a === 'Roof' && b !== 'Roof') return -1;
                if (b === 'Roof' && a !== 'Roof') return 1;
              }
              return a.localeCompare(b);
            });
            return sortedKeys.map(mat => {
              const collapsed = !!collapsedGroups[mat];
              const groupRows = buckets[mat];
              const doneN = groupRows.filter(r => r._completeness?.complete).length;
              return (
                <div key={mat} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`audit-group-${mat}`}>
                  <button
                    type="button"
                    onClick={() => setCollapsedGroups(prev => ({ ...prev, [mat]: !prev[mat] }))}
                    data-testid={`audit-group-toggle-${mat}`}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                  >
                    <span className="text-sm font-bold text-slate-700">
                      <span className="text-slate-400 mr-1.5">{collapsed ? '▶' : '▼'}</span>
                      {mat}
                      <span className="ml-2 text-xs font-mono text-slate-500">({doneN}/{groupRows.length})</span>
                    </span>
                    <span className="text-xs text-slate-500 font-mono-num">
                      {Math.round(100 * doneN / Math.max(1, groupRows.length))}%
                    </span>
                  </button>
                  {!collapsed && (
                    <div className="p-2 space-y-2 bg-white">
                      {groupRows.map(row => (
                        <AuditCard
                          key={row._id}
                          row={row}
                          schema={schema}
                          optionsByField={optionsByField}
                          recipeCost={recipeCostByRowId[row._id]}
                          onPatch={patchRow}
                          onAddOption={addOption}
                          onRenameOption={renameOption}
                          onDeleteOption={deleteOption}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            });
          })()
        ) : (
          visibleRows.map(row => (
            <AuditCard
              key={row._id}
              row={row}
              schema={schema}
              optionsByField={optionsByField}
              recipeCost={recipeCostByRowId[row._id]}
              onPatch={patchRow}
              onAddOption={addOption}
              onRenameOption={renameOption}
              onDeleteOption={deleteOption}
            />
          ))
        )}
      </div>
    </div>
  );
}

function AuditCard({ row, schema, optionsByField, recipeCost, onPatch, onAddOption, onRenameOption, onDeleteOption }) {
  const comp = row._completeness || { pct: 0, complete: false, missing: [] };
  // Iter259c — type_name inline edit
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(row.type_name || '');
  useEffect(() => { setNameDraft(row.type_name || ''); }, [row.type_name]);
  const commitName = () => {
    setEditingName(false);
    const v = nameDraft.trim();
    if (v && v !== row.type_name) onPatch(row._id, 'type_name', v);
  };
  return (
    <div
      data-testid={`audit-card-${row._id}`}
      className={`bg-white border rounded-lg p-4 shadow-sm transition-colors ${comp.complete ? 'border-emerald-200' : 'border-amber-200'}`}
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <DotIndicator pct={comp.pct} />
          {editingName ? (
            <input
              autoFocus
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={e => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') { setNameDraft(row.type_name || ''); setEditingName(false); } }}
              data-testid={`audit-card-name-input-${row._id}`}
              className="flex-1 text-sm font-mono px-2 py-1 border border-slate-300 rounded bg-white"
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditingName(true)}
              data-testid={`audit-card-name-${row._id}`}
              className="flex-1 text-left min-w-0 group inline-flex items-center gap-1.5 hover:bg-slate-50 rounded px-1 -mx-1 py-0.5"
              title="Click to rename"
            >
              <span className="text-sm font-mono text-slate-700 truncate">{row.type_name}</span>
              <span className="opacity-0 group-hover:opacity-100 text-[10px] text-slate-400">✏️</span>
            </button>
          )}
        </div>
        {comp.complete ? (
          comp.accessory ? (
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 ring-1 ring-sky-200" title="Accessory row — excluded from Dektite snap picker">accessory</span>
          ) : (
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">✓ done</span>
          )
        ) : (
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-200">⚠ needs work</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {schema.fields.map(field => (
          <FieldEditor
            key={field.key}
            field={field}
            value={row[field.key]}
            missing={comp.missing?.includes(field.key)}
            options={optionsByField[field.key]}
            onCommit={(v) => onPatch(row._id, field.key, v)}
            onAddOption={(v) => onAddOption(field.key, v)}
            onRenameOption={(oldV, newV) => onRenameOption(field.key, oldV, newV)}
            onDeleteOption={(v) => onDeleteOption(field.key, v)}
          />
        ))}
      </div>

      {/* Read-only rates strip — sanity check without leaving the card. */}
      <div className="mt-3 pt-2 border-t border-slate-100 text-[11px] font-mono-num text-slate-500 flex flex-wrap gap-3 items-center">
        <span>Buy <b className="text-slate-700">${(row.buy_cost ?? 0).toFixed(2)}</b></span>
        <span>Mat <b className="text-slate-700">${(row.materials_rate ?? 0).toFixed(2)}</b></span>
        <span>Lab <b className="text-slate-700">${(row.labour_rate ?? 0).toFixed(2)}</b></span>
        <span>Con <b className="text-orange-700">${(row.contractor_rate ?? 0).toFixed(2)}</b></span>
        {row.supplier_code && <span className="text-slate-400">{row.supplier} · {row.supplier_code}</span>}
        {/* Iter276e — Recipe cost badge. Greyed when no recipe; coloured
            green/red based on drift vs the catalogue's stored buy_cost. */}
        {recipeCost && recipeCost.materials_buy_cost_per_m2_all_in > 0 && (() => {
          const cost = recipeCost.materials_buy_cost_per_m2_all_in;
          const drift = recipeCost.drift_pct || 0;
          const driftHigh = drift > 10;
          return (
            <span
              data-testid={`audit-card-recipe-cost-${row._id}`}
              className={`ml-auto inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ${driftHigh ? 'bg-rose-50 text-rose-700 ring-rose-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200'}`}
              title={`Recipe-derived materials cost. Drift vs catalogue buy_cost = ${drift}%`}
            >
              🧪 ${cost.toFixed(2)}/m²
              {driftHigh && <span className="font-mono normal-case">⚠{drift}%</span>}
              {recipeCost.incomplete > 0 && <span className="font-mono normal-case" title={`${recipeCost.incomplete} entries missing inputs`}>⚠{recipeCost.incomplete}</span>}
            </span>
          );
        })()}
        <Link
          to={`/recipe-editor/${row._id}`}
          data-testid={`audit-card-recipe-link-${row._id}`}
          className={`${recipeCost && recipeCost.materials_buy_cost_per_m2_all_in > 0 ? '' : 'ml-auto'} inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200 hover:bg-violet-100`}
          title="Define multi-supplier material recipe for this catalogue row"
        >
          <span aria-hidden>🧪</span> Recipe
        </Link>
      </div>
    </div>
  );
}

function FieldEditor({ field, value, missing, options, onCommit, onAddOption, onRenameOption, onDeleteOption }) {
  const [local, setLocal] = useState(value ?? (field.kind === 'bool' ? false : ''));
  const [managing, setManaging] = useState(false);
  useEffect(() => { setLocal(value ?? (field.kind === 'bool' ? false : '')); }, [value, field.kind]);

  const commit = (v) => { setLocal(v); onCommit(v); };
  const labelClass = `block text-[10px] font-bold uppercase tracking-wider mb-1 ${missing ? 'text-rose-600' : 'text-slate-500'}`;
  const requiredFlag = field.required && missing ? <span className="ml-1 text-rose-500">⚠ required</span> : null;
  const opts = options || [];
  const manageable = field.kind === 'pill' || field.kind === 'dropdown';

  return (
    <div data-testid={`audit-field-${field.key}`}>
      <div className="flex items-center justify-between gap-2">
        <label className={labelClass}>{field.label}{requiredFlag}</label>
        <div className="flex items-center gap-1 mb-1">
          {/* Iter260n — All / Clear quick actions now live next to the
              title for multipill fields. Smaller, less visual than the
              previous inline pills. */}
          {field.kind === 'multipill' && opts.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => commit([...opts])}
                data-testid={`audit-field-${field.key}-select-all`}
                className="text-[9px] leading-none px-1 py-0.5 rounded text-emerald-700 hover:bg-emerald-50"
                title="Select every option"
              >all</button>
              <button
                type="button"
                onClick={() => commit([])}
                data-testid={`audit-field-${field.key}-clear-all`}
                className="text-[9px] leading-none px-1 py-0.5 rounded text-rose-600 hover:bg-rose-50"
                title="Clear every selection"
              >×</button>
            </>
          )}
          {manageable && (
            <button
              type="button"
              onClick={() => setManaging(m => !m)}
              data-testid={`audit-field-${field.key}-manage-btn`}
              className="text-[9px] text-slate-400 hover:text-slate-700"
              title="Add / rename / delete options"
            >⚙</button>
          )}
        </div>
      </div>
      {field.kind === 'pill' && (
        <div className="flex gap-1.5 flex-wrap">
          {opts.map(opt => (
            <button
              key={opt}
              type="button"
              data-testid={`audit-field-${field.key}-${opt}`}
              onClick={() => commit(opt)}
              className={`px-2 py-1 rounded text-xs font-semibold ring-1 transition-colors ${
                local === opt
                  ? 'bg-slate-700 text-white ring-slate-700'
                  : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >{opt}</button>
          ))}
        </div>
      )}
      {/* Iter260h — multi-value chip selector. Stores the value as an
          array. Clicking a pill toggles it in/out. Empty array = "no
          selection" (treated as unset for completeness purposes).
          Iter260n — All / Clear moved to title row for less visual
          clutter. */}
      {field.kind === 'multipill' && (() => {
        const arr = Array.isArray(local) ? local : (local ? [local] : []);
        const toggle = (opt) => {
          const next = arr.includes(opt) ? arr.filter(v => v !== opt) : [...arr, opt];
          commit(next);
        };
        return (
          <div className="flex gap-1.5 flex-wrap items-center">
            {opts.map(opt => (
              <button
                key={opt}
                type="button"
                data-testid={`audit-field-${field.key}-${opt}`}
                onClick={() => toggle(opt)}
                className={`px-2 py-1 rounded text-xs font-semibold ring-1 transition-colors ${
                  arr.includes(opt)
                    ? 'bg-indigo-600 text-white ring-indigo-600'
                    : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
                }`}
              >{opt}</button>
            ))}
            {arr.length === 0 && (
              <span className="text-[10px] text-slate-400 italic self-center">
                none selected
              </span>
            )}
          </div>
        );
      })()}
      {field.kind === 'dropdown' && (
        <select
          data-testid={`audit-field-${field.key}-select`}
          value={local || ''}
          onChange={e => commit(e.target.value)}
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-white"
        >
          <option value="">— select —</option>
          {opts.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      )}
      {field.kind === 'number' && (
        <input
          type="number"
          data-testid={`audit-field-${field.key}-input`}
          value={local ?? ''}
          onChange={e => setLocal(e.target.value)}
          onBlur={() => commit(local === '' ? null : Number(local))}
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-white"
        />
      )}
      {field.kind === 'text' && (
        <input
          type="text"
          data-testid={`audit-field-${field.key}-input`}
          value={local ?? ''}
          placeholder={field.placeholder || ''}
          onChange={e => setLocal(e.target.value)}
          onBlur={() => commit(local)}
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-white"
        />
      )}
      {field.kind === 'bool' && (
        <label className="inline-flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={!!local}
            onChange={e => commit(e.target.checked)}
            data-testid={`audit-field-${field.key}-checkbox`}
            className="w-4 h-4"
          />
          <span>{local ? 'Yes' : 'No'}</span>
        </label>
      )}
      {/* Iter259c — Manage popover: add / rename / delete options inline.
          Clicking ⚙ above toggles this strip. Rename uses prompt() for
          simplicity (a one-click input would be nicer but the prompt
          is universal + works on mobile too). */}
      {managing && manageable && (
        <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded space-y-1" data-testid={`audit-field-${field.key}-manage-panel`}>
          {opts.map(opt => (
            <div key={opt} className="flex items-center gap-1 text-[11px]">
              <span className="flex-1 font-mono">{opt}</span>
              <button
                type="button"
                onClick={() => { const n = window.prompt(`Rename "${opt}" to:`, opt); if (n) onRenameOption(opt, n); }}
                data-testid={`audit-field-${field.key}-rename-${opt}`}
                className="px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-slate-200 rounded"
              >✏️ rename</button>
              <button
                type="button"
                onClick={() => onDeleteOption(opt)}
                data-testid={`audit-field-${field.key}-delete-${opt}`}
                className="px-1.5 py-0.5 text-[10px] text-rose-600 hover:bg-rose-100 rounded"
              >🗑️ delete</button>
            </div>
          ))}
          <AddOptionInput onAdd={onAddOption} fieldKey={field.key} />
        </div>
      )}
    </div>
  );
}

function AddOptionInput({ onAdd, fieldKey }) {
  const [v, setV] = useState('');
  const commit = () => { if (v.trim()) { onAdd(v.trim()); setV(''); } };
  return (
    <div className="flex items-center gap-1 pt-1 border-t border-slate-200">
      <input
        type="text"
        value={v}
        placeholder="New option…"
        onChange={e => setV(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commit(); }}
        data-testid={`audit-field-${fieldKey}-new-input`}
        className="flex-1 px-1.5 py-0.5 border border-slate-200 rounded text-[11px]"
      />
      <button
        type="button"
        onClick={commit}
        data-testid={`audit-field-${fieldKey}-new-add`}
        className="px-1.5 py-0.5 text-[10px] text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded font-bold"
      >+ Add</button>
    </div>
  );
}
